import React, { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../services/api';
import useInvalidate from '../../hooks/useInvalidate';
import { fixClienteRow, fixText } from '../../utils/textEncoding';
import './clientes.css';

const EMPTY = {
  nombre: '',
  documento: '',
  telefono1: '',
  telefono2: '',
  email: '',
  ciudad: '',
  zona: '',
  direccion: '',
  vendedor_id: '',
  tipo: 'negocio',
};

const EMPTY_SEDE_EDIT = {
  etiqueta: '',
  direccion: '',
  ciudad: '',
  zona: '',
  telefono1: '',
  vendedor_id: '',
};

const PAGE_SIZE = 50;

function sedesBtnLabel(count) {
  const n = Number(count) || 0;
  if (n <= 0) return '+ Sede';
  return n === 1 ? '1 sede' : `${n} sedes`;
}

export default function ClientesPage({ pin, onToast }) {
  const [clientes, setClientes] = useState([]);
  const [vendedores, setVendedores] = useState([]);
  const [ciudades, setCiudades] = useState([]);
  const [zonas, setZonas] = useState([]);
  /** Texto en el input (no dispara API al tipear). */
  const [qInput, setQInput] = useState('');
  /** Texto aplicado con Filtrar / Enter. */
  const [appliedQ, setAppliedQ] = useState('');
  const [ciudad, setCiudad] = useState('');
  const [zona, setZona] = useState('');
  const [vendedorId, setVendedorId] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  // Formulario manual cerrado por defecto.
  const [nuevoAbierto, setNuevoAbierto] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [sedesByPadre, setSedesByPadre] = useState({});
  const [sedesLoadingId, setSedesLoadingId] = useState(null);
  const [editingSedeId, setEditingSedeId] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_SEDE_EDIT);
  const [savingSede, setSavingSede] = useState(false);
  const [openCreateForm, setOpenCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_SEDE_EDIT);
  const loadSeq = useRef(0);
  const facetsBootstrapped = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api
      .getVendedores()
      .then((vend) => {
        if (cancelled) return;
        setVendedores((vend.data || []).filter((v) => v.activo !== 0 && v.activo !== false));
      })
      .catch(() => {
        /* lista de vendedores es auxiliar */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const load = useCallback(() => {
    const seq = ++loadSeq.current;
    setLoading(true);
    const vendParam =
      vendedorId === 'none' ? 'none' : vendedorId ? Number(vendedorId) : undefined;
    // Facets solo al inicio o al cambiar ciudad (zonas). Evita 2ª query pesada en cada búsqueda.
    const wantFacets = !facetsBootstrapped.current || Boolean(ciudad);
    api
      .getAdminSucursales({
        q: appliedQ || undefined,
        ciudad: ciudad || undefined,
        zona: zona || undefined,
        vendedor_id: vendParam,
        tipo: tipoFiltro || undefined,
        limit: PAGE_SIZE,
        offset,
        facets: wantFacets ? 1 : undefined,
      })
      .then((suc) => {
        if (seq !== loadSeq.current) return;
        // API ya excluye sedes; filtro defensivo por si el hosting aún no se actualizó.
        const rows = (suc.data || [])
          .map(fixClienteRow)
          .filter((c) => !(c.es_sede || c.padre_id));
        setClientes(rows);
        setTotal(Number(suc.total) || rows.length);
        if (suc.facets?.ciudades?.length) {
          setCiudades(suc.facets.ciudades.map(fixText).filter(Boolean));
          facetsBootstrapped.current = true;
        }
        if (ciudad) {
          setZonas((suc.facets?.zonas || []).map(fixText).filter(Boolean));
        } else if (suc.facets) {
          setZonas([]);
        }
      })
      .catch((err) => {
        if (seq !== loadSeq.current) return;
        setClientes([]);
        setTotal(0);
        onToast?.(err.message || 'No se pudieron cargar clientes', 'error');
      })
      .finally(() => {
        if (seq === loadSeq.current) setLoading(false);
      });
  }, [appliedQ, ciudad, zona, vendedorId, tipoFiltro, offset, onToast]);

  useEffect(() => {
    load();
  }, [load]);

  useInvalidate(['client', 'seller'], load);

  function setField(key, value) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function applyFilters() {
    const next = qInput.trim();
    if (offset !== 0) setOffset(0);
    if (next === appliedQ && offset === 0) load();
    else setAppliedQ(next);
  }

  function bumpSedesCount(padreId, delta) {
    setClientes((prev) =>
      prev.map((c) => {
        if (Number(c.id) !== Number(padreId)) return c;
        const next = Math.max(0, (Number(c.sedes_count) || 0) + delta);
        return { ...c, sedes_count: next };
      })
    );
  }

  async function toggleSedes(cliente) {
    if (cliente?.es_sede || cliente?.padre_id) return;
    if (cliente?.es_cliente_final || cliente?.tipo === 'final') return;
    const id = Number(cliente.id);
    if (!(id > 0)) return;
    if (expandedId === id) {
      setExpandedId(null);
      setEditingSedeId(null);
      setOpenCreateForm(false);
      return;
    }
    setExpandedId(id);
    setEditingSedeId(null);
    setOpenCreateForm(false);
    setCreateForm({
      ...EMPTY_SEDE_EDIT,
      ciudad: cliente.ciudad || '',
      zona: cliente.zona || '',
      telefono1: cliente.telefono1 || cliente.telefono || '',
      vendedor_id: cliente.vendedor_id ? String(cliente.vendedor_id) : '',
    });
    if (sedesByPadre[id]) return;
    setSedesLoadingId(id);
    try {
      const res = await api.getSedes(id);
      setSedesByPadre((prev) => ({
        ...prev,
        [id]: (res.data || []).map(fixClienteRow),
      }));
    } catch (err) {
      onToast?.(err.message || 'No se pudieron cargar sedes', 'error');
      setSedesByPadre((prev) => ({ ...prev, [id]: [] }));
    } finally {
      setSedesLoadingId(null);
    }
  }

  function startEditSede(sede) {
    setEditingSedeId(sede.id);
    setEditForm({
      etiqueta: sede.etiqueta || sede.sede_etiqueta || '',
      direccion: sede.direccion || '',
      ciudad: sede.ciudad || '',
      zona: sede.zona || '',
      telefono1: sede.telefono1 || sede.telefono || '',
      vendedor_id: sede.vendedor_id ? String(sede.vendedor_id) : '',
    });
  }

  async function guardarSede(padre, sede) {
    setSavingSede(true);
    try {
      const res = await api.actualizarSede(padre.id, sede.id, {
        etiqueta: editForm.etiqueta.trim() || null,
        direccion: editForm.direccion.trim() || null,
        ciudad: editForm.ciudad.trim() || null,
        zona: editForm.zona.trim() || null,
        telefono1: editForm.telefono1.trim() || null,
        vendedor_id: editForm.vendedor_id || null,
      });
      const updated = fixClienteRow(res.data || {});
      setSedesByPadre((prev) => ({
        ...prev,
        [padre.id]: (prev[padre.id] || []).map((s) =>
          Number(s.id) === Number(sede.id) ? { ...s, ...updated } : s
        ),
      }));
      setEditingSedeId(null);
      onToast?.('Sede actualizada');
    } catch (err) {
      onToast?.(err.message || 'No se pudo guardar la sede', 'error');
    } finally {
      setSavingSede(false);
    }
  }

  async function quitarSede(padre, sede) {
    if (!window.confirm('¿Quitar esta dirección/sede?')) return;
    try {
      await api.eliminarSede(padre.id, sede.id);
      const nextList = (sedesByPadre[padre.id] || []).filter((s) => s.id !== sede.id);
      setSedesByPadre((prev) => ({ ...prev, [padre.id]: nextList }));
      bumpSedesCount(padre.id, -1);
      if (editingSedeId === sede.id) setEditingSedeId(null);
      onToast?.('Sede quitada');
    } catch (err) {
      onToast?.(err.message || 'No se pudo quitar', 'error');
    }
  }

  async function crearSedeNueva(padre) {
    if (!createForm.direccion.trim() && !createForm.etiqueta.trim()) {
      onToast?.('Escribe la dirección o un nombre de sede', 'error');
      return;
    }
    setSavingSede(true);
    try {
      const res = await api.crearSede(padre.id, {
        etiqueta: createForm.etiqueta.trim() || undefined,
        direccion: createForm.direccion.trim() || undefined,
        ciudad: createForm.ciudad.trim() || undefined,
        zona: createForm.zona.trim() || undefined,
        telefono1: createForm.telefono1.trim() || undefined,
        vendedor_id: createForm.vendedor_id || undefined,
      });
      const created = fixClienteRow(res.data || {});
      setSedesByPadre((prev) => ({
        ...prev,
        [padre.id]: [...(prev[padre.id] || []), created],
      }));
      bumpSedesCount(padre.id, 1);
      setOpenCreateForm(false);
      setCreateForm({
        ...EMPTY_SEDE_EDIT,
        ciudad: padre.ciudad || '',
        zona: padre.zona || '',
        telefono1: padre.telefono1 || padre.telefono || '',
        vendedor_id: padre.vendedor_id ? String(padre.vendedor_id) : '',
      });
      onToast?.('Sede creada');
    } catch (err) {
      onToast?.(err.message || 'No se pudo crear la sede', 'error');
    } finally {
      setSavingSede(false);
    }
  }

  async function guardar(e) {
    e.preventDefault();
    if (!form.nombre.trim()) {
      onToast?.('El nombre es obligatorio', 'error');
      return;
    }
    setSaving(true);
    try {
      const vend =
        form.vendedor_id && form.vendedor_id !== 'none'
          ? Number(form.vendedor_id)
          : null;
      await api.crearSucursal({
        nombre: form.nombre.trim(),
        documento: form.documento.trim() || null,
        telefono1: form.telefono1.trim() || null,
        telefono2: form.telefono2.trim() || null,
        email: form.email.trim() || null,
        ciudad: form.ciudad.trim() || null,
        zona: form.zona.trim() || null,
        direccion: form.direccion.trim() || null,
        vendedor_id: vend,
        tipo: form.tipo === 'final' ? 'final' : 'negocio',
      });
      onToast?.(form.tipo === 'final' ? 'Cliente final creado' : 'Cliente creado');
      setForm(EMPTY);
      setOffset(0);
      load();
    } catch (err) {
      onToast?.(err.message || 'No se pudo crear', 'error');
    } finally {
      setSaving(false);
    }
  }

  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="panel clientes-page">
      <header className="panel-head">
        <div>
          <h2>Clientes</h2>
        </div>
      </header>

      <section className="form-card">
        <div className="collapsible-head">
          <h3 style={{ margin: 0 }}>Nuevo cliente</h3>
          <button
            type="button"
            className="btn-ghost collapsible-toggle"
            onClick={() => setNuevoAbierto((v) => !v)}
            aria-expanded={nuevoAbierto}
          >
            {nuevoAbierto ? 'Minimizar' : 'Abrir formulario'}
          </button>
        </div>
        {nuevoAbierto ? (
          <form onSubmit={guardar} style={{ marginTop: 14 }}>
            <div className="form-grid">
              <label className="full">
                Tipo
                <div className="cliente-tipo-toggle" role="group" aria-label="Tipo de cliente">
                  <button
                    type="button"
                    className={form.tipo !== 'final' ? 'active' : ''}
                    onClick={() => setField('tipo', 'negocio')}
                  >
                    Negocio / salón
                  </button>
                  <button
                    type="button"
                    className={form.tipo === 'final' ? 'active' : ''}
                    onClick={() => setField('tipo', 'final')}
                  >
                    Cliente final
                  </button>
                </div>
                <em className="cliente-tipo-hint">
                  {form.tipo === 'final'
                    ? 'Persona que compra para sí. No tiene sedes.'
                    : 'Salón, tienda o negocio. Puede tener sedes.'}
                </em>
              </label>
              <label>
                Nombre *
                <input
                  value={form.nombre}
                  onChange={(e) => setField('nombre', e.target.value)}
                  required
                  placeholder={form.tipo === 'final' ? 'Nombre de la persona' : 'Nombre o negocio'}
                />
              </label>
              <label>
                Vendedor
                <select
                  value={form.vendedor_id}
                  onChange={(e) => setField('vendedor_id', e.target.value)}
                >
                  <option value="">Sin asignar (todos lo ven)</option>
                  {vendedores.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.nombre}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Documento
                <input value={form.documento} onChange={(e) => setField('documento', e.target.value)} />
              </label>
              <label>
                Teléfono 1
                <input value={form.telefono1} onChange={(e) => setField('telefono1', e.target.value)} />
              </label>
              <label>
                Teléfono 2
                <input value={form.telefono2} onChange={(e) => setField('telefono2', e.target.value)} />
              </label>
              <label>
                Email
                <input value={form.email} onChange={(e) => setField('email', e.target.value)} />
              </label>
              <label>
                Ciudad
                <input value={form.ciudad} onChange={(e) => setField('ciudad', e.target.value)} />
              </label>
              <label>
                Zona / barrio
                <input value={form.zona} onChange={(e) => setField('zona', e.target.value)} />
              </label>
              <label className="full">
                Dirección
                <input value={form.direccion} onChange={(e) => setField('direccion', e.target.value)} />
              </label>
            </div>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Guardando…' : 'Crear cliente'}
            </button>
          </form>
        ) : null}
      </section>

      <div className="pedidos-admin-filters" style={{ marginTop: 20 }}>
        <label className="date-field">
          <span>Ciudad</span>
          <select
            value={ciudad}
            onChange={(e) => {
              setCiudad(e.target.value);
              setZona('');
              setOffset(0);
            }}
          >
            <option value="">Todas</option>
            {ciudades.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="date-field">
          <span>Zona</span>
          <select
            value={zona}
            onChange={(e) => {
              setZona(e.target.value);
              setOffset(0);
            }}
            disabled={!ciudad}
          >
            <option value="">Todas</option>
            {zonas.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </label>
        <label className="date-field">
          <span>Vendedor</span>
          <select
            value={vendedorId}
            onChange={(e) => {
              setVendedorId(e.target.value);
              setOffset(0);
            }}
          >
            <option value="">Todos</option>
            <option value="none">Sin asignar</option>
            {vendedores.map((v) => (
              <option key={v.id} value={v.id}>
                {v.nombre}
              </option>
            ))}
          </select>
        </label>
        <input
          type="search"
          placeholder="Buscar nombre, código, documento…"
          value={qInput}
          onChange={(e) => setQInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              applyFilters();
            }
          }}
        />
        <button type="button" onClick={applyFilters} disabled={loading}>
          Filtrar
        </button>
      </div>

      <div className="pedidos-admin-chips productos-filter-chips" role="group" aria-label="Tipo de cliente" style={{ marginTop: 12 }}>
        <button
          type="button"
          className={`chip-btn ${!tipoFiltro ? 'active' : ''}`}
          onClick={() => { setTipoFiltro(''); setOffset(0); }}
        >
          Todos
        </button>
        <button
          type="button"
          className={`chip-btn ${tipoFiltro === 'negocio' ? 'active' : ''}`}
          onClick={() => { setTipoFiltro('negocio'); setOffset(0); }}
        >
          Negocios
        </button>
        <button
          type="button"
          className={`chip-btn ${tipoFiltro === 'final' ? 'active' : ''}`}
          onClick={() => { setTipoFiltro('final'); setOffset(0); }}
        >
          Cliente final
        </button>
      </div>

      <p className="muted" style={{ marginTop: 8 }}>
        {loading ? 'Cargando…' : `${total} cliente${total === 1 ? '' : 's'}`}
      </p>

      {!loading && !clientes.length ? <p className="muted">Sin clientes</p> : null}

      {!loading && clientes.length > 0 ? (
        <div className="table-wrap">
          <table className="visitas-table responsive-cards">
            <thead>
              <tr>
                <th>Código</th>
                <th>Nombre</th>
                <th>Ciudad</th>
                <th>Zona</th>
                <th>Teléfono</th>
                <th>Vendedor</th>
                <th>Sedes</th>
              </tr>
            </thead>
            <tbody>
              {clientes.map((c) => {
                const open = expandedId === c.id;
                const sedes = sedesByPadre[c.id] || [];
                const count = Number(c.sedes_count) || 0;
                const esFinal = c.es_cliente_final || c.tipo === 'final';
                return (
                  <React.Fragment key={c.id}>
                    <tr>
                      <td data-label="Código">
                        <span className={esFinal ? 'cliente-final-pill' : 'cliente-padre-pill'}>
                          {esFinal ? 'Cliente final' : 'Cliente'}
                        </span>{' '}
                        {c.codigo || '—'}
                      </td>
                      <td data-label="Nombre">{c.display_nombre || c.nombre}</td>
                      <td data-label="Ciudad">{c.ciudad || '—'}</td>
                      <td data-label="Zona">{c.zona || '—'}</td>
                      <td data-label="Teléfono">{c.telefono1 || c.telefono || '—'}</td>
                      <td data-label="Vendedor">{c.vendedor_nombre || 'Sin asignar'}</td>
                      <td data-label="Sedes">
                        {esFinal ? (
                          <span className="muted">—</span>
                        ) : (
                          <button
                            type="button"
                            className={`btn-ghost btn-sm${count <= 0 && !open ? ' sede-add-hint' : ''}`}
                            onClick={() => toggleSedes(c)}
                          >
                            {open ? 'Ocultar' : sedesBtnLabel(count)}
                          </button>
                        )}
                      </td>
                    </tr>
                    {open ? (
                      <tr className="cliente-sedes-admin-row">
                        <td colSpan={7}>
                          {sedesLoadingId === c.id ? (
                            <p className="muted" style={{ margin: 0 }}>
                              Cargando sedes…
                            </p>
                          ) : (
                            <>
                              {!sedes.length ? (
                                <p className="muted" style={{ margin: '0 0 12px' }}>
                                  Sin direcciones/sedes extras. Agrega una abajo.
                                </p>
                              ) : (
                                <div className="table-wrap cliente-sedes-nested-wrap">
                                  <table className="visitas-table cliente-sedes-nested-table">
                                    <thead>
                                      <tr>
                                        <th>Tipo</th>
                                        <th>Etiqueta</th>
                                        <th>Ciudad</th>
                                        <th>Zona</th>
                                        <th>Teléfono</th>
                                        <th>Vendedor</th>
                                        <th>Dirección</th>
                                        <th></th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {sedes.map((s) => {
                                        const editing = editingSedeId === s.id;
                                        return (
                                          <tr key={s.id} className="cliente-row-sede">
                                            <td data-label="Tipo">
                                              <span className="cliente-sede-pill">Sede</span>
                                            </td>
                                            {editing ? (
                                              <>
                                                <td data-label="Etiqueta">
                                                  <input
                                                    className="sede-edit-input"
                                                    value={editForm.etiqueta}
                                                    onChange={(e) =>
                                                      setEditForm((f) => ({
                                                        ...f,
                                                        etiqueta: e.target.value,
                                                      }))
                                                    }
                                                    placeholder="Local, Bodega…"
                                                  />
                                                </td>
                                                <td data-label="Ciudad">
                                                  <input
                                                    className="sede-edit-input"
                                                    value={editForm.ciudad}
                                                    onChange={(e) =>
                                                      setEditForm((f) => ({
                                                        ...f,
                                                        ciudad: e.target.value,
                                                      }))
                                                    }
                                                  />
                                                </td>
                                                <td data-label="Zona">
                                                  <input
                                                    className="sede-edit-input"
                                                    value={editForm.zona}
                                                    onChange={(e) =>
                                                      setEditForm((f) => ({
                                                        ...f,
                                                        zona: e.target.value,
                                                      }))
                                                    }
                                                  />
                                                </td>
                                                <td data-label="Teléfono">
                                                  <input
                                                    className="sede-edit-input"
                                                    value={editForm.telefono1}
                                                    onChange={(e) =>
                                                      setEditForm((f) => ({
                                                        ...f,
                                                        telefono1: e.target.value,
                                                      }))
                                                    }
                                                  />
                                                </td>
                                                <td data-label="Vendedor">
                                                  <select
                                                    className="sede-edit-input"
                                                    value={editForm.vendedor_id}
                                                    onChange={(e) =>
                                                      setEditForm((f) => ({
                                                        ...f,
                                                        vendedor_id: e.target.value,
                                                      }))
                                                    }
                                                  >
                                                    <option value="">Sin asignar</option>
                                                    {vendedores.map((v) => (
                                                      <option key={v.id} value={v.id}>
                                                        {v.nombre}
                                                      </option>
                                                    ))}
                                                  </select>
                                                </td>
                                                <td data-label="Dirección">
                                                  <input
                                                    className="sede-edit-input"
                                                    value={editForm.direccion}
                                                    onChange={(e) =>
                                                      setEditForm((f) => ({
                                                        ...f,
                                                        direccion: e.target.value,
                                                      }))
                                                    }
                                                  />
                                                </td>
                                                <td data-label="Acciones" className="sede-edit-actions">
                                                  <button
                                                    type="button"
                                                    className="btn-primary btn-sm"
                                                    disabled={savingSede}
                                                    onClick={() => guardarSede(c, s)}
                                                  >
                                                    {savingSede ? 'Guardando…' : 'Guardar'}
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="btn-ghost btn-sm"
                                                    disabled={savingSede}
                                                    onClick={() => setEditingSedeId(null)}
                                                  >
                                                    Cancelar
                                                  </button>
                                                </td>
                                              </>
                                            ) : (
                                              <>
                                                <td data-label="Etiqueta">
                                                  {s.sede_etiqueta || s.etiqueta || 'Sede'}
                                                </td>
                                                <td data-label="Ciudad">{s.ciudad || '—'}</td>
                                                <td data-label="Zona">{s.zona || '—'}</td>
                                                <td data-label="Teléfono">
                                                  {s.telefono1 || s.telefono || '—'}
                                                </td>
                                                <td data-label="Vendedor">
                                                  {s.vendedor_nombre ||
                                                    c.vendedor_nombre ||
                                                    'Sin asignar'}
                                                </td>
                                                <td data-label="Dirección">{s.direccion || '—'}</td>
                                                <td data-label="Acciones" className="sede-edit-actions">
                                                  <button
                                                    type="button"
                                                    className="btn-ghost btn-sm"
                                                    onClick={() => startEditSede(s)}
                                                  >
                                                    Editar
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="btn-ghost btn-sm"
                                                    onClick={() => quitarSede(c, s)}
                                                  >
                                                    Quitar
                                                  </button>
                                                </td>
                                              </>
                                            )}
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              {openCreateForm ? (
                                <form
                                  className="cliente-sedes-create-form"
                                  onSubmit={(e) => {
                                    e.preventDefault();
                                    crearSedeNueva(c);
                                  }}
                                >
                                  <h4>+ Agregar dirección / sede</h4>
                                  <div className="form-grid sede-create-grid">
                                    <label>
                                      Nombre de sede
                                      <input
                                        value={createForm.etiqueta}
                                        onChange={(e) =>
                                          setCreateForm((f) => ({
                                            ...f,
                                            etiqueta: e.target.value,
                                          }))
                                        }
                                        placeholder="Local, Bodega…"
                                      />
                                    </label>
                                    <label>
                                      Ciudad
                                      <input
                                        value={createForm.ciudad}
                                        onChange={(e) =>
                                          setCreateForm((f) => ({
                                            ...f,
                                            ciudad: e.target.value,
                                          }))
                                        }
                                      />
                                    </label>
                                    <label>
                                      Zona
                                      <input
                                        value={createForm.zona}
                                        onChange={(e) =>
                                          setCreateForm((f) => ({
                                            ...f,
                                            zona: e.target.value,
                                          }))
                                        }
                                      />
                                    </label>
                                    <label>
                                      Teléfono
                                      <input
                                        value={createForm.telefono1}
                                        onChange={(e) =>
                                          setCreateForm((f) => ({
                                            ...f,
                                            telefono1: e.target.value,
                                          }))
                                        }
                                      />
                                    </label>
                                    <label>
                                      Vendedor
                                      <select
                                        value={createForm.vendedor_id}
                                        onChange={(e) =>
                                          setCreateForm((f) => ({
                                            ...f,
                                            vendedor_id: e.target.value,
                                          }))
                                        }
                                      >
                                        <option value="">Heredar del cliente</option>
                                        {vendedores.map((v) => (
                                          <option key={v.id} value={v.id}>
                                            {v.nombre}
                                          </option>
                                        ))}
                                      </select>
                                    </label>
                                    <label className="full">
                                      Dirección *
                                      <input
                                        value={createForm.direccion}
                                        onChange={(e) =>
                                          setCreateForm((f) => ({
                                            ...f,
                                            direccion: e.target.value,
                                          }))
                                        }
                                        placeholder="Calle, barrio, referencia…"
                                        required={!createForm.etiqueta.trim()}
                                      />
                                    </label>
                                  </div>
                                  <div className="sede-edit-actions">
                                    <button
                                      type="submit"
                                      className="btn-primary btn-sm"
                                      disabled={savingSede}
                                    >
                                      {savingSede ? 'Guardando…' : 'Guardar sede'}
                                    </button>
                                    <button
                                      type="button"
                                      className="btn-ghost btn-sm"
                                      disabled={savingSede}
                                      onClick={() => setOpenCreateForm(false)}
                                    >
                                      Cancelar
                                    </button>
                                  </div>
                                </form>
                              ) : (
                                <button
                                  type="button"
                                  className="btn-ghost cliente-sedes-add"
                                  onClick={() => setOpenCreateForm(true)}
                                >
                                  + Agregar dirección / sede
                                </button>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {total > PAGE_SIZE ? (
        <div className="pager">
          <button
            type="button"
            disabled={offset <= 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
          >
            ← Anterior
          </button>
          <span>
            Página {page} / {pages}
          </span>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Siguiente →
          </button>
        </div>
      ) : null}
    </div>
  );
}
